import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAlertProfile } from "../../lib/queries";
import { SEVERITY_COLOR } from "../../lib/format";
import { Spinner } from "../../components/ui";

const SERIES: { key: "critical" | "high" | "medium" | "low" | "advisory"; color: string }[] = [
  { key: "critical", color: SEVERITY_COLOR.critical },
  { key: "high", color: SEVERITY_COLOR.high },
  { key: "medium", color: SEVERITY_COLOR.medium },
  { key: "low", color: SEVERITY_COLOR.low },
  { key: "advisory", color: SEVERITY_COLOR.advisory },
];

function bucketLabel(iso: string, granularity: "hour" | "day", multiDay: boolean): string {
  const d = new Date(iso);
  if (granularity === "day") {
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
  }
  const time = d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  // Hour buckets spanning more than one day need the day to disambiguate.
  return multiDay ? `${d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" })} ${time}` : time;
}

export default function AlertProfileChart() {
  const { data, isLoading } = useAlertProfile();
  if (isLoading) return <div className="h-[220px] p-6"><Spinner /></div>;

  const granularity = data?.granularity ?? "hour";
  const buckets = data?.rows ?? [];
  const multiDay =
    buckets.length > 1 &&
    new Date(buckets[0].bucket).toDateString() !==
      new Date(buckets[buckets.length - 1].bucket).toDateString();
  const rows = buckets.map((d) => ({
    ...d,
    t: bucketLabel(d.bucket, granularity, multiDay),
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={rows} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
        <defs>
          {SERIES.map((s) => (
            <linearGradient key={s.key} id={`g-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.55} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.05} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" />
        <XAxis dataKey="t" tick={{ fill: "#64748B", fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis tick={{ fill: "#64748B", fontSize: 10 }} allowDecimals={false} />
        <Tooltip
          contentStyle={{
            background: "#0D1F33",
            border: "1px solid rgba(148,163,184,0.2)",
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        {SERIES.map((s) => (
          <Area
            key={s.key}
            type="monotone"
            dataKey={s.key}
            stackId="1"
            stroke={s.color}
            fill={`url(#g-${s.key})`}
            strokeWidth={1.5}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
